"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const eigeneFlaecheVorn =
  "pointer-coarse:before:absolute pointer-coarse:before:inset-0 pointer-coarse:before:z-[1] pointer-coarse:before:pointer-events-auto pointer-coarse:disabled:z-[1]";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // `primary`, nicht `foreground`: die monochrome Optik gehoert ins Theme,
        // nicht in den Code. Mit `bg-foreground` sah der Standard-Button in JEDEM
        // Theme schwarz/weiss aus — die Akzentfarbe eines Themes erschien nie.
        // Im Janeway-Theme ist primary 9 % (hell) bzw. 98 % (dunkel) und damit
        // praktisch deckungsgleich mit foreground; dort aendert sich nichts.
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        // Erbt die Schrift der Flaeche und zeigt sich als Verweis ueber die
        // Unterstreichung. `text-primary` war eine FLAECHENfarbe als Text: in
        // 1410 von 3096 gerechneten Faellen am `<body>` unter 4,5 : 1, schlechtester
        // 1,01 (claude hell, Menue unter „eine Flaeche"). Auch eine Mischung mit
        // dem Vordergrund trug nicht ueberall (20 % primary: 100 Faelle) —
        // `primary` liegt auf der Erhebung nicht am Pol ihrer Schrift.
        link: "text-current underline underline-offset-4 hover:decoration-2",
      },
      size: {
        // Textknoepfe sind breit genug; am groben Zeiger fehlt nur die Hoehe (PIM-Audit
        // 2026-10-22: 36 bzw. 28 px). Dasselbe unsichtbare ::after wie bei `icon`, nur
        // senkrecht — 36 + 2 x 4 und 28 + 2 x 8 = 44. `relative` steht unbedingt, damit
        // twMerge es fallen laesst, wenn der Aufrufer `absolute` setzt.
        default: `relative h-9 px-4 py-2 pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-1 ${eigeneFlaecheVorn}`,
        sm: `relative h-7 px-3 text-xs pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-2 ${eigeneFlaecheVorn}`,
        lg: "h-11 px-6 text-base",
        // M-097: am groben Zeiger ragt ein unsichtbares ::after bis auf 44 px hinaus;
        // Optik und Layout am Schreibtisch bleiben. Das ::after malt ueber den
        // FRUEHEREN Nachbarn — deshalb deckt ein ::before mit z-[1] den eigenen Knopf
        // ab, sonst loest dessen Rand den Nachbarn aus. Deaktiviert macht opacity-50
        // den Knopf zum Stapelkontext, der das ::before einsperrt — daher dort z-[1]
        // (Waechter knopf-trefferflaeche-am-handy).
        icon: `relative h-9 w-9 pointer-coarse:after:absolute pointer-coarse:after:-inset-1 ${eigeneFlaecheVorn}`,
        "icon-sm": `relative h-7 w-7 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 ${eigeneFlaecheVorn}`,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element (e.g. wrapping a Link). */
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants, type ButtonProps };
